import debugx from "debugx";
import { Position, Subscription, TrackingMode, TrackingData } from "../types";
import { TrackingProvider } from "./trackingProvider";
import { DeviceTrackingProvider } from "./deviceTrackingProvider";
import { SimulationTrackingProvider } from "./simulationTrackingProvider";

const debug = debugx("bikedash:tracking-service");

export interface TrackingServiceConfig {
  mode: TrackingMode;
  autoSwitchToSimulation?: boolean;
  preferSimulationWhenAvailable?: boolean;
}

export class TrackingService {
  private static instance: TrackingService;
  private providers = new Map<string, TrackingProvider>();
  private activeProvider: TrackingProvider | null = null;
  private subscribers = new Set<(data: TrackingData) => void>();
  private errorSubscribers = new Set<(error: Error | null) => void>();
  private transientErrorSubscribers = new Set<() => void>();
  private config: TrackingServiceConfig;
  private currentData: TrackingData | null = null;
  private lastError: Error | null = null;
  private lastTransientErrorTime: number = 0;

  static getInstance(config?: TrackingServiceConfig): TrackingService {
    if (!TrackingService.instance) {
      TrackingService.instance = new TrackingService(config);
    }
    return TrackingService.instance;
  }

  constructor(config: TrackingServiceConfig = { mode: "auto" }) {
    this.config = config;
    this.initializeProviders();
    debug("Tracking service initialized", { config });
  }

  private initializeProviders(): void {
    // Register built-in providers
    this.registerProvider(new DeviceTrackingProvider());
    this.registerProvider(new SimulationTrackingProvider());

    debug("Registered providers:", Array.from(this.providers.keys()));
  }

  registerProvider(provider: TrackingProvider): void {
    if (this.providers.has(provider.id)) {
      debug.warn("Provider already registered:", provider.id);
      return;
    }

    this.providers.set(provider.id, provider);

    // Subscribe to provider updates
    provider.subscribe((data: TrackingData) => {
      debug("Provider data received:", {
        providerId: provider.id,
        isActive: this.activeProvider?.id === provider.id,
        hasPosition: !!data.position,
        positionLat: data.position?.lat,
        positionLng: data.position?.lng,
      });
      // Only forward data if this is the active provider
      if (this.activeProvider?.id === provider.id) {
        // Check for transient error (no position while still active)
        if (
          data.isActive === false &&
          data.position === null &&
          this.currentData?.position
        ) {
          debug(
            "Transient GPS error detected (was active with position, now no position)",
          );
          this.lastTransientErrorTime = Date.now();
          this.notifyTransientErrorSubscribers();
        }

        this.currentData = data;
        this.notifySubscribers(data);
      }
    });

    debug("Provider registered:", provider.id);
  }

  unregisterProvider(providerId: string): void {
    const provider = this.providers.get(providerId);
    if (!provider) return;

    // Stop provider if it's active
    if (this.activeProvider?.id === providerId) {
      this.stop();
    }

    provider.destroy();
    this.providers.delete(providerId);
    debug("Provider unregistered:", providerId);
  }

  getProvider(providerId: string): TrackingProvider | null {
    return this.providers.get(providerId) || null;
  }

  getActiveProvider(): TrackingProvider | null {
    return this.activeProvider;
  }

  getAvailableProviders(): TrackingProvider[] {
    return Array.from(this.providers.values());
  }

  async setActiveProvider(providerId: string): Promise<void> {
    const provider = this.providers.get(providerId);
    if (!provider) {
      throw new Error(`Provider not found: ${providerId}`);
    }

    // Stop current provider if any
    if (this.activeProvider) {
      this.activeProvider.stop();
    }

    // Check if new provider is available
    const isAvailable = await provider.isAvailable();
    if (!isAvailable) {
      throw new Error(`Provider not available: ${providerId}`);
    }

    // Start new provider
    try {
      await provider.start();
      this.activeProvider = provider;
      this.config.mode = provider.type as TrackingMode;

      // Clear error on successful start
      this.clearError();

      debug("Active provider changed:", {
        providerId,
        providerName: provider.name,
        type: provider.type,
      });
    } catch (error) {
      debug.error("Failed to start provider:", providerId, error);
      this.setError(error as Error);
      throw error;
    }
  }

  private setError(error: Error | null): void {
    this.lastError = error;
    this.notifyErrorSubscribers(error);
  }

  getLastError(): Error | null {
    return this.lastError;
  }

  clearError(): void {
    this.setError(null);
  }

  onError(callback: (error: Error | null) => void): Subscription {
    this.errorSubscribers.add(callback);

    // Immediately call with current error if available
    if (this.lastError) {
      callback(this.lastError);
    }

    return {
      unsubscribe: () => {
        this.errorSubscribers.delete(callback);
      },
    };
  }

  private notifyErrorSubscribers(error: Error | null): void {
    debug("Notifying error subscribers:", {
      error: error?.message,
      subscriberCount: this.errorSubscribers.size,
    });

    this.errorSubscribers.forEach((callback) => {
      try {
        callback(error);
      } catch (err) {
        debug.error("Error in tracking service error subscriber:", err);
      }
    });
  }

  onTransientError(callback: () => void): Subscription {
    this.transientErrorSubscribers.add(callback);

    return {
      unsubscribe: () => {
        this.transientErrorSubscribers.delete(callback);
      },
    };
  }

  private notifyTransientErrorSubscribers(): void {
    debug("Notifying transient error subscribers:", {
      subscriberCount: this.transientErrorSubscribers.size,
    });

    this.transientErrorSubscribers.forEach((callback) => {
      try {
        callback();
      } catch (err) {
        debug.error(
          "Error in tracking service transient error subscriber:",
          err,
        );
      }
    });
  }

  getLastTransientErrorTime(): number {
    return this.lastTransientErrorTime;
  }

  async start(mode?: TrackingMode): Promise<void> {
    const targetMode = mode || this.config.mode;
    debug("Starting tracking service", { mode: targetMode });

    if (targetMode === "auto") {
      await this.startAutoMode();
    } else {
      await this.setActiveProvider(targetMode);
    }
  }

  private async startAutoMode(): Promise<void> {
    debug("Starting auto mode");

    // Check if simulation is already active (from previous session)
    const simulationProvider = this.providers.get(
      "simulation",
    ) as SimulationTrackingProvider;
    if (simulationProvider) {
      const simState = simulationProvider.getSimulationState();
      if (simState.isActive) {
        debug("Simulation already active, using simulation provider");
        await this.setActiveProvider("simulation");
        return;
      }
    }

    // Try device provider first
    try {
      await this.setActiveProvider("device");
      debug("Auto mode: using device provider");
    } catch (error) {
      debug.error("Device location provider failed:", error);
      const err = new Error("No tracking providers available");
      this.setError(err);
      throw err;
    }
  }

  stop(): void {
    if (this.activeProvider) {
      debug("Stopping active provider:", this.activeProvider.id);
      this.activeProvider.stop();
      this.activeProvider = null;

      // Notify subscribers that tracking stopped
      const stoppedData: TrackingData = {
        position: null,
        isActive: false,
        source: "unknown",
      };
      this.currentData = stoppedData;
      this.notifySubscribers(stoppedData);
    }
  }

  pause(): void {
    if (this.activeProvider && "pause" in this.activeProvider) {
      (this.activeProvider as any).pause();
      debug("Paused active provider:", this.activeProvider.id);
    }
  }

  resume(): void {
    if (this.activeProvider && "resume" in this.activeProvider) {
      (this.activeProvider as any).resume();
      debug("Resumed active provider:", this.activeProvider.id);
    }
  }

  getCurrentData(): TrackingData | null {
    return this.currentData;
  }

  getCurrentPosition(): Position | null {
    return this.currentData?.position || null;
  }

  isActive(): boolean {
    return this.activeProvider?.isActive() || false;
  }

  getMode(): TrackingMode {
    return this.config.mode;
  }

  setConfig(config: Partial<TrackingServiceConfig>): void {
    this.config = { ...this.config, ...config };
    debug("Config updated:", this.config);
  }

  subscribe(callback: (data: TrackingData) => void): Subscription {
    this.subscribers.add(callback);

    // Immediately call with current data if available
    if (this.currentData) {
      debug("Calling subscriber with current data:", {
        hasPosition: !!this.currentData.position,
        positionLat: this.currentData.position?.lat,
        positionLng: this.currentData.position?.lng,
      });
      callback(this.currentData);
    }

    return {
      unsubscribe: () => {
        this.subscribers.delete(callback);
      },
    };
  }

  private notifySubscribers(data: TrackingData): void {
    const callId = Math.random().toString(36).substring(2, 11);
    debug("Notifying subscribers:", {
      callId,
      source: data.source,
      hasPosition: !!data.position,
      positionLat: data.position?.lat,
      positionLng: data.position?.lng,
      subscriberCount: this.subscribers.size,
    });

    this.subscribers.forEach((callback) => {
      try {
        debug("Calling subscriber", { callId });
        callback(data);
      } catch (error) {
        debug.error("Error in tracking service subscriber:", error);
      }
    });
  }

  destroy(): void {
    debug("Destroying tracking service");

    this.stop();

    // Destroy all providers
    this.providers.forEach((provider) => {
      provider.destroy();
    });

    this.providers.clear();
    this.subscribers.clear();
    this.activeProvider = null;
    this.currentData = null;
  }
}

export function getTrackingService(): TrackingService {
  return TrackingService.getInstance();
}
